/* Violet Eyes header: mobile menu drawer, submenus, search and likes shortcuts. */
if (!customElements.get('ve-header')) {
  /* Submenus (side menu + / – and the phone / hamburger menu): the list glides open or closed in
     height instead of jumping, and its links fade in one after another (BNJMN, 14 Oct: smoother).
     A click during the movement turns it around from where it is. Reduced motion: no movement. */
  const VE_EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
  const veSlideSub = (sub, expand) => {
    const from = sub.hidden ? 0 : sub.getBoundingClientRect().height;
    if (sub.veAnim) sub.veAnim.cancel();
    sub.veAnim = null;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !sub.animate) {
      sub.hidden = !expand;
      return;
    }
    sub.hidden = false;
    const to = expand ? sub.scrollHeight : 0;
    sub.style.overflow = 'hidden';
    const anim = sub.animate(
      expand
        ? [{ height: `${from}px` }, { height: `${to}px` }]
        : [{ height: `${from}px`, opacity: 1 }, { height: '0px', opacity: 0 }],
      { duration: expand ? 420 : 320, easing: VE_EASE }
    );
    sub.veAnim = anim;
    if (expand) {
      Array.from(sub.children).forEach((item, index) => {
        item.animate(
          [
            { opacity: 0, transform: 'translateY(-6px)' },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 360, delay: 60 + index * 35, easing: VE_EASE, fill: 'backwards' }
        );
      });
    }
    anim.onfinish = () => {
      sub.veAnim = null;
      sub.style.overflow = '';
      if (!expand) sub.hidden = true;
    };
  };

  class VeHeader extends HTMLElement {
    connectedCallback() {
      // Product pages (desktop): main categories open and close with + / –
      this.querySelectorAll('.ve-side [data-ve-nav-toggle]').forEach((button) => {
        button.addEventListener('click', () => {
          const sub = this.querySelector(`#${button.getAttribute('aria-controls')}`);
          if (!sub) return;
          const expand = button.getAttribute('aria-expanded') !== 'true';
          veSlideSub(sub, expand);
          this.querySelectorAll(`.ve-side [aria-controls="${sub.id}"]`).forEach((control) =>
            control.setAttribute('aria-expanded', String(expand))
          );
        });
      });

      this.drawer = this.querySelector('#VeMenuDrawer');
      this.openButton = this.querySelector('[data-ve-menu-open]');
      if (!this.drawer || !this.openButton) return;

      this.onKeyUp = (event) => {
        if (event.code === 'Escape') this.close();
      };

      this.openButton.addEventListener('click', () => this.open());
      this.drawer.querySelectorAll('[data-ve-menu-close]').forEach((button) =>
        button.addEventListener('click', () => this.close())
      );

      this.drawer.querySelectorAll('[data-ve-sub-toggle]').forEach((button) => {
        button.addEventListener('click', () => this.toggleSub(button));
      });

      this.drawer.querySelectorAll('.ve-mnav__row > a.ve-mnav__label').forEach((link) => {
        const button = link.parentElement.querySelector('[data-ve-sub-toggle]');
        if (!button) return;
        link.addEventListener('click', (event) => {
          if (window.matchMedia('(min-width: 990px)').matches) return;
          if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          if (button.getAttribute('aria-expanded') === 'true') return;
          event.preventDefault();
          this.toggleSub(button);
        });
      });

      // Snag puts its likes toggle in .ve-likes; the drawer offers a shortcut to it.
      // Phones: in the menu list below "Visit the Studio"; the About page's desktop panel: in the foot.
      this.drawer.querySelectorAll('[data-ve-open-likes]').forEach((likesButton) => {
        const showLikes = () => {
          if (this.querySelector('.like-app-drawer-toggle')) likesButton.hidden = false;
        };
        showLikes();
        new MutationObserver(showLikes).observe(this.querySelector('.ve-likes'), { childList: true });
        likesButton.addEventListener('click', () => {
          this.close(false);
          setTimeout(() => {
            const toggle = document.querySelector('.like-app-drawer-toggle');
            if (toggle) toggle.click();
          });
        });
      });

      // Stagger order for the opening animation
      this.drawer.querySelectorAll('.ve-mnav > li').forEach((item, index) => item.style.setProperty('--ve-i', index));
      const foot = this.drawer.querySelector('.ve-drawer__foot');
      if (foot) foot.style.setProperty('--ve-i', this.drawer.querySelectorAll('.ve-mnav > li').length);
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      // Close the drawer if the window grows past the mobile layout.
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      // (Not on the page with the desktop hamburger menu, where the panel is used on desktop too.)
      this.burger = this.classList.contains('ve-header--burger');
      // Cart on narrower computer screens (Header › "Cart: hamburger menu on computers narrower than")
      const burgerBelow = Number(this.dataset.veBurgerBelow) || 0;
      if (burgerBelow > 1000 && !this.burger) {
        const narrow = window.matchMedia(`(max-width: ${burgerBelow - 0.02}px)`);
        const applyBurger = () => {
          if (this.isOpen) this.close(false);
          this.burger = narrow.matches;
          this.classList.toggle('ve-header--burger', this.burger);
        };
        narrow.addEventListener('change', applyBurger);
        applyBurger();
      }
      this.desktopQuery.addEventListener('change', (event) => {
        if (event.matches && !this.burger) this.close(false);
      });

      // Desktop hamburger panel: a click on the dimmed page beside it only closes it.
      this.onOutsideClick = (event) => {
        if (this.drawer.contains(event.target) || this.openButton.contains(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
        this.close(false);
      };
    }

    // Computers (hamburger panel): the panel's close icon is moved onto the menu button's lines, so the same three lines
    // are seen before and after the panel fades in, and then turn into the X (BNJMN, 28 Oct). Measured while both are flat.
    alignCloseIcon() {
      const close = this.drawer.querySelector('.ve-drawer__close');
      if (!close) return;
      close.style.translate = '';
      if (!this.burger || !window.matchMedia('(min-width: 990px)').matches) return;
      const from = this.openButton.querySelector('.ve-burger');
      const to = close.querySelector('.ve-burger');
      if (!from || !to) return;
      const a = from.getBoundingClientRect();
      const b = to.getBoundingClientRect();
      const dx = Math.round((a.left - b.left) * 10) / 10;
      const dy = Math.round((a.top - b.top) * 10) / 10;
      if (Math.abs(dx) < 400 && Math.abs(dy) < 400) close.style.translate = `${dx}px ${dy}px`;
    }

    open() {
      const cartCopy = this.drawer.querySelector('[data-ve-cart-copy]');
      const cartLive = document.getElementById('cart-icon-bubble');
      if (cartCopy && cartLive) cartCopy.innerHTML = cartLive.innerHTML;

      clearTimeout(this.hideTimer);
      document.documentElement.classList.remove('ve-drawer-closing', 've-drawer-quick');
      this.drawer.hidden = false;
      void this.drawer.offsetWidth; // start the slide-in from the closed position
      this.alignCloseIcon();
      this.drawer.classList.add('is-open');
      this.isOpen = true;
      this.openButton.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('ve-drawer-open');
      document.addEventListener('keyup', this.onKeyUp);
      if (this.burger) document.addEventListener('click', this.onOutsideClick, true);
      if (typeof trapFocus === 'function') {
        trapFocus(this.drawer, this.drawer.querySelector('[data-ve-menu-close]'));
      } else {
        this.drawer.focus();
      }
    }

    close(returnFocus = true, quick = false) {
      if (!this.isOpen) return;
      this.drawer.querySelectorAll('ve-compact-search').forEach((search) => search.closeSuggestions());
      this.isOpen = false;
      this.drawer.classList.remove('is-open');
      clearTimeout(this.hideTimer);
      if (this.reduceMotion && this.reduceMotion.matches) {
        this.drawer.hidden = true;
      } else {
        // the page's category list waits for the panel to fade out before it fades back in (ve-desktop-layout.css)
        document.documentElement.classList.add('ve-drawer-closing');
        // a category picked in the menu: the panel clears away fast so the new cards show at once (29 Oct, BNJMN)
        if (quick) document.documentElement.classList.add('ve-drawer-quick');
        // hide after the slide-out / fade-out (0.4s phones, 0.7s computers)
        this.hideTimer = setTimeout(() => {
          document.documentElement.classList.remove('ve-drawer-closing', 've-drawer-quick');
          if (!this.isOpen) this.drawer.hidden = true;
        }, quick ? 200 : 740);
      }
      this.openButton.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('ve-drawer-open');
      document.removeEventListener('keyup', this.onKeyUp);
      document.removeEventListener('click', this.onOutsideClick, true);
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(returnFocus ? this.openButton : null);
      } else if (returnFocus) {
        this.openButton.focus();
      }
    }

    toggleSub(button) {
      const sub = this.drawer.querySelector(`#${button.getAttribute('aria-controls')}`);
      if (!sub) return;
      const expand = button.getAttribute('aria-expanded') !== 'true';
      veSlideSub(sub, expand);
      this.drawer
        .querySelectorAll(`[aria-controls="${sub.id}"]`)
        .forEach((control) => control.setAttribute('aria-expanded', String(expand)));
    }
  }

  customElements.define('ve-header', VeHeader);
}

/* Snag's likes drawer writes "$" before every price and the "Wishlist total", whatever the
   store currency. On a shekel store, show them like the rest of the site: ₪190 (Hebrew: 190 ₪).
   (The lasting fix is in the Snag app itself: format prices with the shop's currency.) */
(function () {
  if (!window.Shopify || !Shopify.currency || Shopify.currency.active !== 'ILS') return;
  const hebrew = document.documentElement.lang === 'he';
  const PRICE = '.like-app-drawer-item-price, .like-app-drawer-total span:last-child';
  const format = (text) => {
    const match = text.match(/^\s*\$\s*([\d,]+(?:\.\d+)?)\s*$/);
    if (!match) return null;
    const value = parseFloat(match[1].replace(/,/g, ''));
    if (isNaN(value)) return null;
    let amount = Number.isInteger(value) ? String(value) : value.toFixed(2);
    amount = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return hebrew ? amount + '\u00a0₪' : '₪' + amount;
  };
  const fix = (element) => {
    const targets = element.matches(PRICE) ? [element] : element.querySelectorAll(PRICE);
    targets.forEach((target) => {
      const text = format(target.textContent);
      if (text) target.textContent = text;
    });
  };
  const start = () => {
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) =>
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) fix(node);
        })
      );
    }).observe(document.body, { childList: true, subtree: true });
    fix(document.body);
  };
  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start);
})();

